import { useState, useMemo } from "react";
import { LayoutGrid } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/empty-state";
import { EntityButton } from "@/components/entity-button";
import { TableSkeleton } from "@/components/loading-skeletons";
import { SearchInput } from "@/components/search-input";
import { SortButton } from "@/components/sort-button";
import { RowLink, TableLinkRow } from "@/components/table-link-row";
import { SectionHeader } from "@/components/widgets/section-header";
import { useWidgetsQuery } from "@/hooks/use-widgets";
import { useDataSourcesQuery } from "@/hooks/use-data-sources";

const AUTO_REFRESH_INTERVAL = 30000;

type SortField = "name" | "dataSourceName" | "published";
type SortOrder = "asc" | "desc";

export function WidgetsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  const {
    data: widgets,
    isLoading: widgetsLoading,
    error: widgetsError,
  } = useWidgetsQuery({
    refetchInterval: AUTO_REFRESH_INTERVAL,
  });
  const { data: dataSources, isLoading: dataSourcesLoading } =
    useDataSourcesQuery();

  const isLoading = widgetsLoading || dataSourcesLoading;

  const dataSourceMap = useMemo(() => {
    if (!dataSources) return new Map();
    return new Map(dataSources.map((ds) => [ds.id, ds.name]));
  }, [dataSources]);

  const enrichedWidgets = useMemo(() => {
    if (!widgets) return [];
    return widgets.map((widget) => ({
      ...widget,
      dataSourceName: widget.data_source_id
        ? dataSourceMap.get(widget.data_source_id) || widget.data_source_id
        : "None",
    }));
  }, [widgets, dataSourceMap]);

  const filteredAndSortedWidgets = useMemo(() => {
    if (!enrichedWidgets) return [];

    let result = enrichedWidgets;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((widget) =>
        widget.name.toLowerCase().includes(query)
      );
    }

    result = [...result].sort((a, b) => {
      let comparison: number;
      if (sortField === "published") {
        comparison = (a.published ? 1 : 0) - (b.published ? 1 : 0);
      } else {
        comparison = a[sortField].localeCompare(b[sortField]);
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });

    return result;
  }, [enrichedWidgets, searchQuery, sortField, sortOrder]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortDirection = (field: SortField) =>
    sortField === field ? sortOrder : null;

  return (
    <div className="flex flex-1 flex-col gap-4">
      <SectionHeader
        actions={widgets && <EntityButton entityName="Widget" to="/widgets/new" />}
      />

      <div className="flex flex-1 flex-col">
        {widgets && widgets.length > 0 && (
          <div className="border-b pb-4">
            <SearchInput
              placeholder="Search widgets..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        )}

        {isLoading && <TableSkeleton />}

        {widgetsError && (
          <p className="py-16 text-center text-sm text-destructive">
            Failed to load widgets
          </p>
        )}

        {widgets && (
          <>
            {filteredAndSortedWidgets.length === 0 && (
              <EmptyState
                icon={LayoutGrid}
                title={searchQuery.trim() ? "No results" : "No widgets found"}
                description={
                  searchQuery.trim()
                    ? "No widgets match your search."
                    : "Widgets you create will appear here."
                }
              >
                {!searchQuery.trim() && (
                  <EntityButton entityName="Widget" to="/widgets/new" />
                )}
              </EmptyState>
            )}

            {filteredAndSortedWidgets.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <SortButton
                        label="Name"
                        direction={getSortDirection("name")}
                        onClick={() => handleSort("name")}
                      />
                    </TableHead>
                    <TableHead className="md:w-64">
                      <SortButton
                        label="Data Source"
                        direction={getSortDirection("dataSourceName")}
                        onClick={() => handleSort("dataSourceName")}
                      />
                    </TableHead>
                    <TableHead className="md:w-44">
                      <SortButton
                        label="Status"
                        direction={getSortDirection("published")}
                        onClick={() => handleSort("published")}
                      />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedWidgets.map((widget) => (
                    <TableLinkRow key={widget.id} to={`/widgets/${widget.id}`}>
                      <TableCell>
                        <RowLink to={`/widgets/${widget.id}`}>{widget.name}</RowLink>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {widget.dataSourceName}
                      </TableCell>
                      <TableCell>
                        {widget.published ? (
                          <Badge variant="success">Published</Badge>
                        ) : (
                          <Badge variant="secondary">Draft</Badge>
                        )}
                      </TableCell>
                    </TableLinkRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </div>
    </div>
  );
}
